import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import profileService from '../services/api/profileService';
import permissionService from '../services/api/permissionService';
import authService from '../services/auth/authService';
import { useAuth } from './AuthContext';

const ProfileContext = createContext();

export function ProfileProvider({ children }) {
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsProfileSelection, setNeedsProfileSelection] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [activationChecked, setActivationChecked] = useState(false);

  const { user, isAuthenticated } = useAuth();

  const checkActivationStatus = useCallback(async () => {
    try {
      const activationStatus = await authService.checkActivationStatus();
      const requiresActivation =
        activationStatus.requiresActivation && activationStatus.skipProfileSelection;

      setNeedsActivation(requiresActivation);
      setActivationChecked(true);
    } catch (err) {
      setNeedsActivation(false);
      setActivationChecked(true);
    }
  }, []);

  // Check activation status when user is authenticated
  useEffect(() => {
    const checkStatus = async () => {
      if (isAuthenticated && user && !activationChecked) {
        // Add a small delay to ensure cookies are fully processed
        await new Promise(resolve => setTimeout(resolve, 100));
        await checkActivationStatus();
      }
    };

    checkStatus();
  }, [isAuthenticated, user, activationChecked, checkActivationStatus]);

  // Load profiles only if user doesn't need activation
  useEffect(() => {
    if (isAuthenticated && user && activationChecked && !needsActivation) {
      // Add a small delay to ensure cookies from the authentication flow
      // are fully processed by the browser before making profile requests
      const timeoutId = setTimeout(() => {
        loadProfiles();
      }, 100);

      // Cleanup timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, user, activationChecked, needsActivation]);

  // Load permissions when active profile changes
  useEffect(() => {
    if (isAuthenticated && activeProfile) {
      loadPermissions();
    }
  }, [isAuthenticated, activeProfile]);

  const loadPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const permissionsData = await permissionService.getPermissions();
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const profiles = await profileService.getAvailableProfiles();
      setAvailableProfiles(profiles);

      // Check current session to see if user already has an active profile
      try {
        const session = await profileService.getCurrentSession();
        setCurrentSession(session);

        // Check if the response indicates profile selection is required
        if (session && session.requiresProfileSelection) {
          // Backend explicitly says profile selection is needed
          setActiveProfile(null);
          setNeedsProfileSelection(true);

          // If only one profile is available, auto-select it
          if (profiles.length === 1) {
            try {
              await profileService.selectProfile(profiles[0].id);
              const updatedSession = await profileService.getCurrentSession();
              setCurrentSession(updatedSession);

              const activeProfile = updatedSession.availableProfiles?.find(
                p => p.id === updatedSession.activeProfileId
              );
              setActiveProfile(activeProfile || null);
              setNeedsProfileSelection(false);
            } catch (autoSelectError) {
              console.error('Failed to auto-select profile:', autoSelectError);
              setNeedsProfileSelection(true);
            }
          }
        } else if (session && session.activeProfileId) {
          // Session has an active profile
          const activeProfile = session.availableProfiles?.find(
            p => p.id === session.activeProfileId
          );

          if (activeProfile) {
            setActiveProfile(activeProfile);
            setNeedsProfileSelection(false);
          } else {
            // Session exists but active profile not found in available profiles
            setActiveProfile(null);
            setCurrentSession(null);
            setNeedsProfileSelection(true);
          }
        } else {
          // Session exists but no active profile ID and no explicit requiresProfileSelection flag
          setActiveProfile(null);
          setCurrentSession(null);
          setNeedsProfileSelection(true);
        }
      } catch (sessionError) {
        // If getCurrentSession fails with an error, check if we should auto-select
        if (profiles.length === 1) {
          // Auto-select the only available profile
          try {
            await profileService.selectProfile(profiles[0].id);
            const session = await profileService.getCurrentSession();
            setCurrentSession(session);

            const activeProfile = session.availableProfiles?.find(
              p => p.id === session.activeProfileId
            );
            setActiveProfile(activeProfile || null);
            setNeedsProfileSelection(false);
          } catch (autoSelectError) {
            console.error('Failed to auto-select profile:', autoSelectError);
            // Fall back to manual selection
            setNeedsProfileSelection(true);
            setActiveProfile(null);
            setCurrentSession(null);
          }
        } else {
          // Multiple profiles available, user needs to select
          // When there's no current session but profiles exist, user MUST select a profile
          if (profiles.length > 0) {
            setNeedsProfileSelection(true);
            setActiveProfile(null);
            setCurrentSession(null);
          } else {
            setNeedsProfileSelection(false);
            setActiveProfile(null);
            setCurrentSession(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load user profiles:', err);
      setError(err);
      setAvailableProfiles([]);
      setNeedsProfileSelection(false);
    } finally {
      setLoading(false);
    }
  };

  const selectProfile = async profileId => {
    try {
      setLoading(true);
      setError(null);

      // Select the profile (this updates the authentication cookie)
      await profileService.selectProfile(profileId);

      // After successful profile selection, get the updated session
      const session = await profileService.getCurrentSession();
      setCurrentSession(session);

      // Find the active profile from the available profiles using the activeProfileId
      const activeProfile = session.availableProfiles?.find(p => p.id === session.activeProfileId);
      setActiveProfile(activeProfile || null);
      setNeedsProfileSelection(false);

      // Dispatch custom event to notify that profile has changed and permissions should be refetched
      window.dispatchEvent(
        new CustomEvent('profileChanged', { detail: { profileId, activeProfile } })
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to select profile:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentSession = useCallback(async () => {
    try {
      const session = await profileService.getCurrentSession();
      setCurrentSession(session);

      // Find the active profile from the available profiles using the activeProfileId
      const activeProfile = session.availableProfiles?.find(p => p.id === session.activeProfileId);
      setActiveProfile(activeProfile || null);
      setNeedsProfileSelection(false);
    } catch (err) {
      console.error('Failed to refresh current session:', err);
      setError(err);
      setNeedsProfileSelection(availableProfiles.length > 0);
    }
  }, [availableProfiles.length]);

  // Clear profile state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setAvailableProfiles([]);
      setActiveProfile(null);
      setCurrentSession(null);
      setNeedsProfileSelection(false);
      setNeedsActivation(false);
      setActivationChecked(false);
      setError(null);
    }
  }, [isAuthenticated]);

  const completeActivation = useCallback(() => {
    setNeedsActivation(false);
  }, []);

  // Helper functions defined first to avoid temporal dead zone issues
  const isSystemProfile = useCallback(() => {
    return !activeProfile?.customerId; // System-wide if no customer ID
  }, [activeProfile?.customerId]);

  const getUserRole = useCallback(() => {
    return activeProfile?.roleName || currentSession?.role || null;
  }, [activeProfile?.roleName, currentSession?.role]);

  const getCustomerId = useCallback(() => {
    if (isSystemProfile()) return null;
    return activeProfile?.customerId || currentSession?.customerId;
  }, [activeProfile?.customerId, currentSession?.customerId, isSystemProfile]);

  // Enhanced permission checking that combines role-based and server permissions
  const hasPermission = useCallback(
    (permissionKey, customerId = null) => {
      const userRole = getUserRole();

      // First check role-based permissions
      const rolePermissions = permissions[userRole];
      if (rolePermissions && rolePermissions[permissionKey] === false) {
        return false;
      }

      // Check customer scope for non-system admins
      if (customerId && userRole !== 'SystemAdministrator') {
        const userCustomerId = getCustomerId();
        if (customerId !== userCustomerId) {
          return false;
        }
      }

      // Check server permissions if available
      if (permissions && Object.keys(permissions).length > 0) {
        return permissions[permissionKey] === 'GRANTED';
      }

      // Fall back to session permissions
      return currentSession?.permissions?.includes(permissionKey) || false;
    },
    [currentSession, permissions, getCustomerId, getUserRole]
  );

  const hasAnyPermission = permissions => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = permissions => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(permission => hasPermission(permission));
  };

  // Check if user has access to specific customer
  const hasCustomerAccess = useCallback(
    customerId => {
      if (!isAuthenticated || !activeProfile) {
        return false;
      }

      const userRole = getUserRole();
      if (userRole === 'SystemAdministrator' || userRole === 'SupportUser') {
        return true;
      }

      const userCustomerId = getCustomerId();
      return customerId === userCustomerId;
    },
    [isAuthenticated, activeProfile, getCustomerId, getUserRole]
  );

  // Compute role checks as boolean values instead of functions
  const isSystemAdmin = useMemo(() => {
    return getUserRole() === 'SystemAdministrator';
  }, [getUserRole]);

  const isCustomerAdmin = useMemo(() => {
    return getUserRole() === 'CustomerAdministrator';
  }, [getUserRole]);

  const isCustomerUser = useMemo(() => {
    return getUserRole() === 'CustomerUser';
  }, [getUserRole]);

  const isSupportUser = useMemo(() => {
    return getUserRole() === 'SupportUser';
  }, [getUserRole]);

  const refetchPermissions = useCallback(() => {
    if (isAuthenticated && activeProfile) {
      loadPermissions();
    }
  }, [isAuthenticated, activeProfile]);

  // Listen for profile change events to refetch permissions
  useEffect(() => {
    const handleProfileChange = () => {
      refetchPermissions();
    };

    window.addEventListener('profileChanged', handleProfileChange);

    return () => {
      window.removeEventListener('profileChanged', handleProfileChange);
    };
  }, [refetchPermissions]);

  // Customer access helpers
  const canAccessCustomer = customerId => hasCustomerAccess(customerId);
  const getAccessibleCustomerId = () => (!isSystemAdmin ? getCustomerId() : null);

  return (
    <ProfileContext.Provider
      value={{
        // Profile state
        availableProfiles,
        activeProfile,
        currentSession,
        loading,
        error,
        needsProfileSelection,

        // Profile actions
        selectProfile,
        loadProfiles,
        refreshCurrentSession,

        // Permission state and actions
        permissions,
        permissionsLoading,
        refetchPermissions,

        // Permission checks
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasCustomerAccess,
        canAccessCustomer,
        getAccessibleCustomerId,

        // Role checks
        isSystemAdmin,
        isCustomerAdmin,
        isCustomerUser,
        isSupportUser,
        getUserRole,
        getCustomerId,

        // Convenience flags
        isLoaded: !loading && isAuthenticated && activationChecked && !needsActivation,
        hasActiveProfile: !!activeProfile,

        // Activation state
        needsActivation,
        activationChecked,
        completeActivation,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

ProfileProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

/**
 * Compatibility hook for code using usePermissionCheck
 * @returns {Object} Object containing permission check functions and role information
 */
export function usePermissionCheck() {
  const {
    hasPermission,
    hasCustomerAccess,
    canAccessCustomer,
    getAccessibleCustomerId,
    isSystemAdmin,
    isCustomerAdmin,
    isCustomerUser,
    isSupportUser,
    getUserRole: userRole,
    getCustomerId: userCustomerId,
  } = useProfile();

  return {
    hasPermission,
    hasCustomerAccess,
    canAccessCustomer,
    getAccessibleCustomerId,
    isSystemAdmin,
    isCustomerAdmin,
    isCustomerUser,
    isSupportUser,
    userRole: userRole(),
    userCustomerId: userCustomerId(),
  };
}

/**
 * Compatibility hook for code using usePermissions
 * @returns {Object} Object containing permission functions and state
 */
export function usePermissions() {
  const {
    permissions,
    permissionsLoading: loading,
    error,
    hasPermission,
    hasCustomerAccess,
    refetchPermissions,
    getUserRole: userRole,
    getCustomerId: userCustomerId,
  } = useProfile();

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasCustomerAccess,
    refetchPermissions,
    userRole: userRole(),
    userCustomerId: userCustomerId(),
  };
}
